/**
 * Punktzahl einer Partie: Amtszeit, Wahlergebnisse, Gesetze und Lage des Landes.
 * Liegt in der Engine, damit Client und Server (verify-run) dieselbe Formel nutzen.
 */
import type { GameState } from './types';
import { poll } from './govern';
import { dateYear } from './util';
import { legFactor, diffKey } from './level12';

export function scoreRun(S: GameState): number {
  const legs = S.career.length;
  const shares = S.career.map((c) => c.s1 ?? c.s0);
  const avgShare = shares.length ? shares.reduce((a, b) => a + b, 0) / shares.length : 0;
  const laws = S.career.reduce((s, c) => s + c.laws, 0);
  const fiscal = Math.max(-30, Math.min(20, S.saldo || 0));
  const debt = Math.max(0, (S.debt || 2950) - 2950);
  const ind = S.ind || {}, ind0 = S.ind0 || {};
  const indicators = Object.keys(ind).reduce((s, k) => s + (ind[k] - (ind0[k] ?? ind[k])) / Math.max(1, Math.abs(ind0[k] ?? 1)) * 100, 0);
  const raw = legs * 1000 + avgShare * 40 + laws * 25 + fiscal * 10 - debt * 0.5 + indicators * 2;
  // Version 12: Faktor je Legislatur nach Erfahrungsstufe (ältere Partien: 1)
  const F = legs && S.career.some((c: any) => c.lv) ? S.career.reduce((s, c) => s + legFactor(c), 0) / legs : 1;
  return Math.max(0, Math.round(raw * F));
}

/** Zusammenfassung einer beendeten Partie für die Bestenliste. */
export function runSummary(S: GameState) {
  const last = S.career[S.career.length - 1];
  const auto = ((S as any).dem?.min ?? 85) < 50; // Version 11: eigene Kategorie, nur bei Bedarf mitgeschickt (ältere Datenbanken kennen die Spalte nicht)
  return {
    ...(auto ? { category: 'autoritaer' } : {}),
    party_name: S.name,
    legislatures: S.career.length,
    final_share: last?.s1 ?? null,
    laws: S.career.reduce((s, c) => s + c.laws, 0),
    score: scoreRun(S),
    difficulty: diffKey(S),
    seed: S.seed ?? 0,
  };
}

/**
 * Kennzahlen einer laufenden Partie für die Live-Liste der Bestenliste.
 * Läuft auf dem Server nach dem Nachspielen (verify-run, Modus „live“), deshalb in der Engine.
 * `running`: Die eigene Partei regiert gerade oder hat die letzte Wahl gewonnen.
 */
export function liveSummary(S: GameState) {
  const s = S as any, C = S.career || [];
  const governs = ['govern', 'review'].includes(S.phase) || (S.leg > 0 && ['campaign', 'night', 'talks'].includes(S.phase)) || (S.phase === 'end' && s.end === 'election' && !!s.won);
  const inOpp = !!s.opp || !!s.chal; // Version 11: Opposition zählt nicht als Regierungszeit
  return {
    running: !S.mp && governs && !inOpp,
    auto: ((S as any).dem?.min ?? 85) < 50,
    party: S.name,
    leg: S.leg - (s.oppLegs || 0) + 1 + (S.phase === 'end' && s.won ? 1 : 0),
    since: C[0]?.a ?? s.legYear ?? 2029,
    year: dateYear(S.m),
    months: S.m,
    poll: Math.round((S.phase === 'end' ? (s.final ?? 0) : poll()) * 10) / 10,
    diff: (S as any).ver >= 12 && (S.cfg as any)?.lvl ? `stufe-${(S.cfg as any).lvl}${(S as any).adjAny ? '-angepasst' : ''}` : S.cfg?.diff ?? 'normal',
    seed: S.seed ?? 0,
  };
}
